import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState } from 'react';
import { throttle } from 'underscore';

import { GeodataModel, GeodataStatus } from '../../../../../typings/api/skymap/rest/v0/.common';
import { FetchGeodataByIdQueryParameters } from '../../../../../typings/api/skymap/rest/v0/geodata';
import { SkyMapAxiosServiceFactory } from '../../../services/axios/skymap-axios-service-factory';
import { iiafe } from '../../../utils/async';
import { isDefined } from '../../../utils/variables';
import { SubscriptionTopic } from '../../../viewer/messaging/pubsub';
import { useErrorHandling } from '../../hooks/use-error-handling';
import { useSubscribeCallback } from '../../hooks/use-subscribe';

const successStatuses: GeodataStatus[] = [
  'generate_pointcloud_finished',
  'generate_skyview_data_finished',
  'generate_tif_finished',
];

const failedStatuses: GeodataStatus[] = [
  'align_photos_failed',
  'generate_pointcloud_failed',
  'generate_skyview_data_failed',
];

const pendingStatuses: GeodataStatus[] = [
  'align_photos_pending',
  'align_photos_preparing',
  'optimize_pending',
  'optimize_pending_reset_alignment',
  'optimize_running',
  'generate_skyview_data_pending',
  'match_photos',
  'align_cameras',
  'optimize_cameras',
  'build_depth_maps',
  'export_points',
  'build_model',
  'build_orthomosaic',
  'export_raster',
  'build_tiled_model',
  'generate_tif_pending',
  'generating_skyview_data',
];

const optimizeStatuses: GeodataStatus[] = ['optimize_pending', 'optimize_running'];

const completedStatus: GeodataStatus[] = [...successStatuses, ...failedStatuses];
const placeMarkerStatuses: GeodataStatus[] = ['created', 'user_move_flags'];

const readyToOptimizeStatuses: GeodataStatus[] = [...placeMarkerStatuses, 'align_photos_failed'];
const readyToStartProcessStatuses: GeodataStatus[] = [...placeMarkerStatuses];
const idleStatuses: GeodataStatus[] = [...completedStatus, ...placeMarkerStatuses];

export const geodataStatuses = {
  placeMarkerStatuses,
  readyToOptimizeStatuses,
  readyToStartProcessStatuses,
  idleStatuses,
  failedStatuses,
  successStatuses,
  optimizeStatuses,
  pendingStatuses,
};

export const alignerGeodataIncludes: FetchGeodataByIdQueryParameters['include'] = [
  'geodata.geoid',
  'geodata.coordinateSystem',
  'geodata.gcpCollection',
  'geodata.resourceUrls',
  'geodata.uploadedBy',
  'geodata.filteredPointCloud',
];

export type UseGeodataModel = Omit<
  GeodataModel,
  'uploadedBy' | 'coordinateSystem' | 'resourceUrls'
> &
  Pick<Required<GeodataModel>, 'uploadedBy' | 'coordinateSystem' | 'resourceUrls'>;

async function getGeodata(geodataId: string): Promise<UseGeodataModel> {
  const response = await SkyMapAxiosServiceFactory.instance
    .createGeodataServiceV0()
    .fetchGeodataById({
      path: {
        geodataId,
      },
      query: {
        include: alignerGeodataIncludes,
      },
    });

  return response.data as UseGeodataModel;
}

export function useGeodata(geodataId: string) {
  const { handleError, clearErrors, buildErrorList } = useErrorHandling();
  const [isGeodataProcessing, setIsGeodataProcessing] = useState(false);

  const { data, refetch, error, isPending, isRefetching } = useQuery({
    queryKey: ['fetchGeodataById', geodataId],
    queryFn: async () => getGeodata(geodataId),
    networkMode: 'always',
    retry: false,
    refetchInterval: false,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    gcTime: 1000 * 60 * 2,
    staleTime: 1000 * 60 * 2,
  });

  const throttledRefetch = useMemo(
    () => throttle(refetch, 2000, { leading: false, trailing: true }),
    [refetch],
  );

  // Kept in a ref so the error-handling effect can reference the latest refetch
  // without listing it as a dependency — an unstable refetch reference (TanStack
  // Query v5 recreates it on each query-state change) would otherwise restart the
  // effect every render and trigger the same render loop fixed in SK-7088.
  const refetchRef = useRef(refetch);
  refetchRef.current = refetch;

  useSubscribeCallback(SubscriptionTopic.OnWebsocketReconnected, () => {
    iiafe(async () => {
      await throttledRefetch();
    });
  });

  useSubscribeCallback(SubscriptionTopic.WebsocketMessageRecieved, (event) => {
    if (event.message === 'GeodataUpdated' && event.geodataId === geodataId) {
      setIsGeodataProcessing(event.state === 'PROCESSING');
    }

    // We only need to refetch if the geodata is created or updated.
    // Deleted geodata will throw 403.
    if (
      (event.message === 'GeodataCreated' || event.message === 'GeodataUpdated') &&
      event.geodataId === geodataId
    ) {
      iiafe(async () => {
        await throttledRefetch();
      });
    }
  });

  useEffect(() => {
    if (isDefined(error)) {
      handleError(error, { retryCallback: () => void refetchRef.current() });
    } else {
      clearErrors();
    }
  }, [handleError, clearErrors, error]);

  useEffect(() => {
    if (isDefined(data)) {
      setIsGeodataProcessing(!idleStatuses.includes(data.status));
    }
  }, [data]);

  const errorList = buildErrorList();

  return {
    geodata: data,
    refetch: throttledRefetch,
    errorList,
    isGeodataProcessing,
    isPending: isPending || isRefetching,
  };
}
